import Swiper from "swiper";
import { EffectCoverflow, Navigation, Pagination } from "swiper/modules";

class PrtsFldsQuestionSlider {
  constructor(selector = "#js-qstn-slider", options = {}, root = document) {
    this.selector = selector;
    this.options = options;
    this.root = root;

    this.target = null;
    this.swiper = null;
    this.resizeTimer = null;
    this.positionRaf = null;

    this.positionClasses = ["--1", "--2", "--3", "--4", "--5"];
    this.desktopQuery = window.matchMedia("(min-width: 768px)");

    this.handleWindowLoad = this.handleWindowLoad.bind(this);
    this.handleResize = this.handleResize.bind(this);
    this.handleSlideStateChange = this.handleSlideStateChange.bind(this);
    this.handleMediaChange = this.handleMediaChange.bind(this);

    this.init();
  }

  init() {
    this.target = this.getTarget();

    if (!this.target) return;

    const prevEl = this.target.querySelector(".swiper-button-prev");
    const nextEl = this.target.querySelector(".swiper-button-next");
    const paginationEl = this.target.querySelector(".swiper-pagination");

    this.swiper = new Swiper(this.target, {
      modules: [EffectCoverflow, Navigation, Pagination],

      loop: true,
      centeredSlides: true,
      slidesPerView: "auto",
      initialSlide: 0,
      speed: 500,
      grabCursor: true,
      watchSlidesProgress: true,
      slideToClickedSlide: true,

      spaceBetween: 16,

      effect: "coverflow",
      coverflowEffect: {
        rotate: 0,
        stretch: 0,
        depth: 80,
        modifier: 1,
        scale: 0.88,
        slideShadows: false,
      },

      navigation: {
        prevEl,
        nextEl,
      },

      pagination: {
        el: paginationEl,
        clickable: true,
      },

      breakpoints: {
        768: {
          spaceBetween: 0,
          coverflowEffect: {
            rotate: 0,
            stretch: -40,
            depth: 140,
            modifier: 1,
            scale: 0.89,
            slideShadows: false,
          },
        },
      },

      observer: true,
      observeParents: true,

      ...this.options,
    });

    this.bindEvents();
    this.updateAfterMount();
    this.requestPositionClassUpdate();
  }

  getTarget() {
    if (this.root && typeof this.root.querySelector === "function") {
      return this.root.querySelector(this.selector);
    }

    return document.querySelector(this.selector);
  }

  bindEvents() {
    window.addEventListener("load", this.handleWindowLoad, false);
    window.addEventListener("resize", this.handleResize, false);

    this.desktopQuery.addEventListener("change", this.handleMediaChange, false);

    this.bindImageLoad();
    this.bindSwiperEvents();
  }

  bindSwiperEvents() {
    if (!this.swiper) return;

    this.swiper.on("slideChange", this.handleSlideStateChange);
    this.swiper.on("slideChangeTransitionStart", this.handleSlideStateChange);
    this.swiper.on("slideChangeTransitionEnd", this.handleSlideStateChange);
    this.swiper.on("transitionEnd", this.handleSlideStateChange);
    this.swiper.on("loopFix", this.handleSlideStateChange);
    this.swiper.on("update", this.handleSlideStateChange);
    this.swiper.on("resize", this.handleSlideStateChange);
  }

  bindImageLoad() {
    if (!this.target) return;

    const images = this.target.querySelectorAll("img");

    images.forEach((image) => {
      if (image.complete) return;

      image.addEventListener(
        "load",
        () => {
          this.update();
          this.requestPositionClassUpdate();
        },
        { once: true },
      );
    });
  }

  handleWindowLoad() {
    this.update();
    this.requestPositionClassUpdate();
  }

  handleResize() {
    window.clearTimeout(this.resizeTimer);

    this.resizeTimer = window.setTimeout(() => {
      this.update();
      this.requestPositionClassUpdate();
    }, 200);
  }

  handleMediaChange() {
    this.requestPositionClassUpdate();
  }

  handleSlideStateChange() {
    this.requestPositionClassUpdate();
  }

  updateAfterMount() {
    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => {
        this.update();
        this.requestPositionClassUpdate();
      });
    });
  }

  isDesktop() {
    return this.desktopQuery.matches;
  }

  requestPositionClassUpdate() {
    if (this.positionRaf) {
      window.cancelAnimationFrame(this.positionRaf);
    }

    this.positionRaf = window.requestAnimationFrame(() => {
      this.updatePositionClasses();
    });
  }

  updatePositionClasses() {
    if (!this.swiper) return;

    this.clearPositionClasses();

    // 767px以下では --1〜--5 を付けない
    if (!this.isDesktop()) return;

    const slides = Array.from(this.swiper.slides);

    if (!slides.length) return;

    const activeIndex = this.swiper.activeIndex;
    const offsets = [-2, -1, 0, 1, 2];

    offsets.forEach((offset, classIndex) => {
      const slide = this.getSlideByOffset(slides, activeIndex, offset);

      if (!slide) return;

      slide.classList.add(this.positionClasses[classIndex]);
    });
  }

  getSlideByOffset(slides, activeIndex, offset) {
    const length = slides.length;

    if (!length) return null;

    const index = (((activeIndex + offset) % length) + length) % length;

    return slides[index];
  }

  clearPositionClasses() {
    if (!this.swiper) return;

    const slides = Array.from(this.swiper.slides);

    slides.forEach((slide) => {
      this.positionClasses.forEach((className) => {
        slide.classList.remove(className);
      });
    });
  }

  update() {
    if (!this.swiper) return;

    this.swiper.update();
    this.swiper.slideToLoop(this.swiper.realIndex, 0, false);
  }

  destroy() {
    window.clearTimeout(this.resizeTimer);

    if (this.positionRaf) {
      window.cancelAnimationFrame(this.positionRaf);
      this.positionRaf = null;
    }

    window.removeEventListener("load", this.handleWindowLoad, false);
    window.removeEventListener("resize", this.handleResize, false);

    this.desktopQuery.removeEventListener(
      "change",
      this.handleMediaChange,
      false,
    );

    this.clearPositionClasses();

    if (!this.swiper) return;

    this.swiper.off("slideChange", this.handleSlideStateChange);
    this.swiper.off("slideChangeTransitionStart", this.handleSlideStateChange);
    this.swiper.off("slideChangeTransitionEnd", this.handleSlideStateChange);
    this.swiper.off("transitionEnd", this.handleSlideStateChange);
    this.swiper.off("loopFix", this.handleSlideStateChange);
    this.swiper.off("update", this.handleSlideStateChange);
    this.swiper.off("resize", this.handleSlideStateChange);

    this.swiper.destroy(true, true);
    this.swiper = null;
    this.target = null;
  }
}

export default PrtsFldsQuestionSlider;
